import { computed, ref } from 'vue'
import type {
  UserStateKeyActiveDb,
  UserStateValueMapActiveDb
} from '@gat-ship/types'
import { DEFAULT_USER_STATE_VALUE_MAP_ACTIVE_DB } from '@gat-ship/types'
import {
  getMultipleItemsRequest,
  removeItemRequest,
  setItemRequest
} from './user-state-api-calls'
import type { GetUserStateActiveDbResponseMap } from '@gat-ship/types'

export function useUserStateActiveDb() {
  const isFetchedKeys = ref<Record<UserStateKeyActiveDb, boolean>>({
    'stakeholders.recently-visited': false,
    'stakeholders.saved-searches': false
  })

  const state = ref<{
    [K in UserStateKeyActiveDb]: UserStateValueMapActiveDb[K]
  }>({
    ...DEFAULT_USER_STATE_VALUE_MAP_ACTIVE_DB
  })

  function setState<K extends UserStateKeyActiveDb>(
    key: K,
    value: UserStateValueMapActiveDb[K]
  ) {
    state.value[key] = value as UserStateValueMapActiveDb[K]
  }

  async function getState<K extends UserStateKeyActiveDb>(
    key: K
  ): Promise<UserStateValueMapActiveDb[K]> {
    try {
      if (!isFetchedKeys.value[key as UserStateKeyActiveDb]) {
        await getMultipleItems([key as UserStateKeyActiveDb])
      }
      return state.value[
        key as UserStateKeyActiveDb
      ] as UserStateValueMapActiveDb[K]
    } catch {
      console.error(`Failed to get state for key ${key}`)
      return DEFAULT_USER_STATE_VALUE_MAP_ACTIVE_DB[key]
    }
  }

  async function getMultipleItems<
    const K extends readonly UserStateKeyActiveDb[]
  >(
    keys: K
  ): Promise<GetUserStateActiveDbResponseMap<K, UserStateValueMapActiveDb>> {
    const result = await getMultipleItemsRequest({
      body: {
        storageScope: 'active-db',
        keys: keys as unknown as string[]
      }
    })
    Object.keys(result).forEach((key) => {
      const entry = result[key]
      if (!entry || entry.status !== 'found') {
        return
      }
      isFetchedKeys.value[key as UserStateKeyActiveDb] = true
      setState(
        key as UserStateKeyActiveDb,
        entry.value as UserStateValueMapActiveDb[UserStateKeyActiveDb]
      )
    })
    return result as GetUserStateActiveDbResponseMap<
      K,
      UserStateValueMapActiveDb
    >
  }

  async function setItem<K extends UserStateKeyActiveDb>(
    key: K,
    value: UserStateValueMapActiveDb[K]
  ): Promise<void> {
    // Best-effort persistence: user-state is a UX cache hint ("remember the
    // user's last selection"), not load-bearing data. A failed network write
    // must not cascade to an unhandled rejection — the optimistic local
    // update is what the UI actually reads.
    try {
      await setItemRequest({
        key,
        queryParams: { storage_scope: 'active-db' },
        body: value
      })
    } catch (error) {
      console.warn(
        `[user-state] failed to persist active-db key "${String(key)}" — keeping local value.`,
        error
      )
    }
    isFetchedKeys.value[key] = true
    setState(key, value)
  }

  async function removeItem<K extends UserStateKeyActiveDb>(
    key: K
  ): Promise<void> {
    await removeItemRequest({
      key,
      queryParams: { storage_scope: 'active-db' }
    })
    isFetchedKeys.value[key] = false
    setState(key, DEFAULT_USER_STATE_VALUE_MAP_ACTIVE_DB[key])
  }

  return {
    state: computed(() => state.value),
    getState,
    getMultipleItems,
    setItem,
    removeItem
  }
}
